import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Search,
  FileText,
  Sparkles,
  Send,
  Clock,
  CheckCircle,
  Loader2,
  X,
  Eye,
  Trash2,
  Filter,
  ChevronDown,
  Download,
} from "lucide-react";

import { articlesApi, sitesApi } from "@/services/api";
import { notify } from "@/stores/notificationStore";
import type { Article, ArticleStatus, PaginatedResponse, Site } from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ArticleGenerationWizard } from "@/components/articles/ArticleGenerationWizard";
import { ArticleProgress } from "@/components/articles/ArticleProgress";
import { ArticleView } from "@/components/articles/ArticleView";
import { PublishModal } from "@/components/modals/PublishModal";
import { cn } from "@/lib/utils";
import type { PublishRequest } from "@/types";

// Helper function to strip HTML tags from content for preview
function stripHtmlTags(html: string): string {
  if (!html) return "";
  // Remove markdown code blocks first
  let text = html.replace(/```[\s\S]*?```/g, "");
  // Remove HTML tags
  text = text.replace(/<[^>]*>/g, "");
  // Decode common HTML entities
  text = text.replace(/&nbsp;/g, " ");
  text = text.replace(/&amp;/g, "&");
  text = text.replace(/&lt;/g, "<");
  text = text.replace(/&gt;/g, ">");
  text = text.replace(/&quot;/g, '"');
  // Normalize whitespace
  text = text.replace(/\s+/g, " ").trim();
  return text;
}

// V3 pipeline status configuration
const statusConfig: Record<
  ArticleStatus,
  { labelKey: string; icon: typeof FileText; color: string; isActive: boolean }
> = {
  draft: {
    labelKey: "articles.status.draft",
    icon: FileText,
    color: "bg-gray-500",
    isActive: false,
  },
  pending: {
    labelKey: "articles.status.pending",
    icon: Clock,
    color: "bg-yellow-500",
    isActive: true,
  },
  // V3 pipeline stages
  planning: {
    labelKey: "articles.status.planning",
    icon: Loader2,
    color: "bg-blue-500",
    isActive: true,
  },
  writing: {
    labelKey: "articles.status.writing",
    icon: Loader2,
    color: "bg-indigo-500",
    isActive: true,
  },
  reviewing: {
    labelKey: "articles.status.reviewing",
    icon: Loader2,
    color: "bg-purple-500",
    isActive: true,
  },
  generating_image: {
    labelKey: "articles.status.generatingImage",
    icon: Loader2,
    color: "bg-cyan-500",
    isActive: true,
  },
  ready: {
    labelKey: "articles.status.ready",
    icon: CheckCircle,
    color: "bg-green-500",
    isActive: false,
  },
  failed: {
    labelKey: "articles.status.failed",
    icon: X,
    color: "bg-red-500",
    isActive: false,
  },
  published: {
    labelKey: "articles.status.published",
    icon: Send,
    color: "bg-purple-500",
    isActive: false,
  },
  // Legacy statuses (for backwards compatibility with old articles)
  optimizing: {
    labelKey: "articles.status.optimizing",
    icon: Loader2,
    color: "bg-purple-500",
    isActive: true,
  },
  finalizing: {
    labelKey: "articles.status.finalizing",
    icon: Loader2,
    color: "bg-orange-500",
    isActive: true,
  },
  researching: {
    labelKey: "articles.status.researching",
    icon: Loader2,
    color: "bg-blue-500",
    isActive: true,
  },
  editing: {
    labelKey: "articles.status.editing",
    icon: Loader2,
    color: "bg-orange-500",
    isActive: true,
  },
  humanizing: {
    labelKey: "articles.status.humanizing",
    icon: Loader2,
    color: "bg-pink-500",
    isActive: true,
  },
};

function getArticleStatus(article: Article): ArticleStatus {
  if (article.status && article.status in statusConfig) {
    return article.status;
  }
  if (article.tags.includes("published")) return "published";
  return "draft";
}

function isArticleGenerating(status: ArticleStatus): boolean {
  return statusConfig[status]?.isActive ?? false;
}

type ViewMode = "list" | "generate" | "progress" | "view";

interface FilterState {
  search: string;
  status: string | null;
  siteId: string | null;
  dateRange: "all" | "today" | "week" | "month";
  minSeoScore: number | null;
  minGeoScore: number | null;
}

export function ArticlesPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<FilterState>({
    search: "",
    status: null,
    siteId: null,
    dateRange: "all",
    minSeoScore: null,
    minGeoScore: null,
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  // Auto-open wizard if ?duplicate= query param is present (from "Duplicate & Edit").
  const hasDuplicateParam = new URLSearchParams(window.location.search).has(
    "duplicate",
  );
  const [viewMode, setViewMode] = useState<ViewMode>(
    hasDuplicateParam ? "generate" : "list",
  );
  const [activeArticleId, setActiveArticleId] = useState<string | null>(null);
  const [activeTopic, setActiveTopic] = useState<string>("");
  const [selectedArticles, setSelectedArticles] = useState<Set<string>>(
    new Set(),
  );
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishArticleId, setPublishArticleId] = useState<string | null>(null);
  const [publishArticleTitle, setPublishArticleTitle] = useState<string>("");
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<PaginatedResponse<Article>>({
    queryKey: ["articles"],
    queryFn: () => articlesApi.list(),
  });

  const { data: sitesData } = useQuery({
    queryKey: ["sites"],
    queryFn: () => sitesApi.list(),
  });

  const sites = sitesData?.items || [];

  const deleteArticle = useMutation({
    mutationFn: (articleId: string) => articlesApi.delete(articleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["articles"] });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => articlesApi.delete(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["articles"] });
      setSelectedArticles(new Set());
    },
  });

  const publishMutation = useMutation({
    mutationFn: async ({
      articleId,
      data,
    }: {
      articleId: string;
      data: PublishRequest;
    }) => {
      const response = await articlesApi.publish(articleId, data);
      // Check if the response indicates failure (API returns 200 with success: false)
      if (!response.success) {
        throw new Error(response.message || "Failed to publish article");
      }
      return response;
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["articles"] });
      setPublishModalOpen(false);
      setPublishArticleId(null);
      // Send success notification
      notify.articlePublished(
        publishArticleTitle,
        response.wp_post_url || undefined,
      );
    },
    onError: (error: Error) => {
      // Send error notification
      notify.error(
        "Publish Failed",
        error.message || "Failed to publish article to WordPress",
      );
    },
  });

  const handlePublishClick = (articleId: string, title: string) => {
    setPublishArticleId(articleId);
    setPublishArticleTitle(title);
    setPublishModalOpen(true);
  };

  const handlePublish = async (articleId: string, data: PublishRequest) => {
    await publishMutation.mutateAsync({ articleId, data });
  };

  const reoptimizeMutation = useMutation({
    mutationFn: async (articleId: string) => {
      return articlesApi.retry(articleId, "writer"); // Retry from writer agent
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["articles"] });
      // Switch to progress view to show reoptimization progress
      if (activeArticleId) {
        setViewMode("progress");
        setActiveTopic("Reoptimizing Article");
      }
    },
  });

  const handleReoptimize = async (articleId: string) => {
    await reoptimizeMutation.mutateAsync(articleId);
  };

  const handleGenerateSuccess = (articleId: string) => {
    setActiveArticleId(articleId);
    setActiveTopic("AI Generated Article");
    setViewMode("progress");
    queryClient.invalidateQueries({ queryKey: ["articles"] });
  };

  const handleProgressComplete = () => {
    queryClient.invalidateQueries({ queryKey: ["articles"] });
  };

  const handleCloseProgress = () => {
    setViewMode("list");
    setActiveArticleId(null);
    queryClient.invalidateQueries({ queryKey: ["articles"] });
  };

  // Filter articles
  const filteredArticles = useMemo(() => {
    if (!data?.items) return [];

    return data.items.filter((article) => {
      // Search filter
      const matchesSearch =
        !filters.search ||
        article.title.toLowerCase().includes(filters.search.toLowerCase()) ||
        article.tags.some((tag) =>
          tag.toLowerCase().includes(filters.search.toLowerCase()),
        ) ||
        article.content.toLowerCase().includes(filters.search.toLowerCase());

      // Status filter
      const status = getArticleStatus(article);
      const matchesStatus = !filters.status || status === filters.status;

      // Site filter
      const matchesSite = !filters.siteId || article.site_id === filters.siteId;

      // Date filter
      let matchesDate = true;
      if (filters.dateRange !== "all") {
        const articleDate = new Date(article.created_at);
        const now = new Date();
        switch (filters.dateRange) {
          case "today":
            matchesDate = articleDate.toDateString() === now.toDateString();
            break;
          case "week": {
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            matchesDate = articleDate >= weekAgo;
            break;
          }
          case "month": {
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            matchesDate = articleDate >= monthAgo;
            break;
          }
        }
      }

      // Score filters
      const matchesSeoScore =
        filters.minSeoScore === null ||
        (article.seo_score ?? 0) >= filters.minSeoScore;
      const matchesGeoScore =
        filters.minGeoScore === null ||
        (article.geo_score ?? 0) >= filters.minGeoScore;

      return (
        matchesSearch &&
        matchesStatus &&
        matchesSite &&
        matchesDate &&
        matchesSeoScore &&
        matchesGeoScore
      );
    });
  }, [data?.items, filters]);

  // Toggle article selection
  const toggleArticleSelection = (articleId: string) => {
    setSelectedArticles((prev) => {
      const next = new Set(prev);
      if (next.has(articleId)) {
        next.delete(articleId);
      } else {
        next.add(articleId);
      }
      return next;
    });
  };

  // Select all visible articles
  const selectAllArticles = () => {
    if (selectedArticles.size === filteredArticles.length) {
      setSelectedArticles(new Set());
    } else {
      setSelectedArticles(new Set(filteredArticles.map((a) => a.id)));
    }
  };

  // Bulk delete
  const handleBulkDelete = () => {
    if (
      confirm(
        `Are you sure you want to delete ${selectedArticles.size} articles?`,
      )
    ) {
      bulkDeleteMutation.mutate(Array.from(selectedArticles));
    }
  };

  // Export selected articles
  const handleExport = () => {
    const articlesToExport = filteredArticles.filter((a) =>
      selectedArticles.has(a.id),
    );
    const exportData = articlesToExport.map((a) => ({
      title: a.title,
      content: a.content,
      meta_title: a.meta_title,
      meta_description: a.meta_description,
      tags: a.tags,
      seo_score: a.seo_score,
      geo_score: a.geo_score,
      human_score: a.human_score,
      word_count: a.word_count,
      status: a.status,
      created_at: a.created_at,
    }));

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `articles-export-${new Date().toISOString().split("T")[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Show article view
  if (viewMode === "view" && activeArticleId) {
    const currentArticle = data?.items.find((a) => a.id === activeArticleId);
    return (
      <>
        <ArticleView
          articleId={activeArticleId}
          onBack={() => {
            setViewMode("list");
            setActiveArticleId(null);
          }}
          onPublish={() =>
            handlePublishClick(
              activeArticleId,
              currentArticle?.title || "Article",
            )
          }
          onReoptimize={() => handleReoptimize(activeArticleId)}
          isReoptimizing={reoptimizeMutation.isPending}
        />
        <PublishModal
          open={publishModalOpen}
          onOpenChange={setPublishModalOpen}
          articleId={publishArticleId || ""}
          articleTitle={publishArticleTitle}
          sites={sites}
          onPublish={handlePublish}
          isPublishing={publishMutation.isPending}
        />
      </>
    );
  }

  // Show progress view for generating article
  if (viewMode === "progress" && activeArticleId) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={handleCloseProgress}>
          &larr; {t("articles.backToArticles")}
        </Button>
        <ArticleProgress
          articleId={activeArticleId}
          topic={activeTopic}
          onComplete={handleProgressComplete}
          onClose={() => {
            setActiveArticleId(activeArticleId);
            setViewMode("view");
            queryClient.invalidateQueries({ queryKey: ["articles"] });
          }}
        />
      </div>
    );
  }

  // Show generate wizard
  if (viewMode === "generate") {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => setViewMode("list")}>
          &larr; {t("articles.backToArticles")}
        </Button>
        <ArticleGenerationWizard
          onSuccess={handleGenerateSuccess}
          onCancel={() => setViewMode("list")}
        />
      </div>
    );
  }

  // Main list view
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t("articles.title")}
          </h1>
          <p className="text-muted-foreground">{t("articles.description")}</p>
        </div>
        <Button onClick={() => setViewMode("generate")}>
          <Sparkles className="me-2 h-4 w-4" />
          {t("articles.generateWithAI")}
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t("articles.searchPlaceholder")}
              value={filters.search}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, search: e.target.value }))
              }
              className="ps-9"
            />
          </div>

          {/* Quick Status Filters */}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={filters.status === null ? "default" : "outline"}
              size="sm"
              onClick={() => setFilters((prev) => ({ ...prev, status: null }))}
            >
              {t("app.all")}
            </Button>
            <Button
              variant={filters.status === "ready" ? "default" : "outline"}
              size="sm"
              onClick={() =>
                setFilters((prev) => ({ ...prev, status: "ready" }))
              }
            >
              {t("articles.status.ready")}
            </Button>
            <Button
              variant={filters.status === "published" ? "default" : "outline"}
              size="sm"
              onClick={() =>
                setFilters((prev) => ({ ...prev, status: "published" }))
              }
            >
              {t("articles.status.published")}
            </Button>
            <Button
              variant={filters.status === "failed" ? "default" : "outline"}
              size="sm"
              onClick={() =>
                setFilters((prev) => ({ ...prev, status: "failed" }))
              }
            >
              {t("articles.status.failed")}
            </Button>
          </div>

          {/* Advanced Filters Toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            <Filter className="me-2 h-4 w-4" />
            {t("app.filters")}
            <ChevronDown
              className={cn(
                "ms-2 h-4 w-4 transition-transform",
                showAdvancedFilters && "rotate-180",
              )}
            />
          </Button>
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Site Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t("articles.filters.site")}
                  </label>
                  <select
                    value={filters.siteId || ""}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        siteId: e.target.value || null,
                      }))
                    }
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                  >
                    <option value="">{t("articles.filters.allSites")}</option>
                    {sites.map((site: Site) => (
                      <option key={site.id} value={site.id}>
                        {site.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date Range Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t("articles.filters.dateRange")}
                  </label>
                  <select
                    value={filters.dateRange}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        dateRange: e.target.value as FilterState["dateRange"],
                      }))
                    }
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                  >
                    <option value="all">{t("articles.filters.allTime")}</option>
                    <option value="today">{t("articles.filters.today")}</option>
                    <option value="week">
                      {t("articles.filters.last7Days")}
                    </option>
                    <option value="month">
                      {t("articles.filters.last30Days")}
                    </option>
                  </select>
                </div>

                {/* Min SEO Score */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t("articles.filters.minSeoScore")}
                  </label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    placeholder={t("articles.filters.any")}
                    value={filters.minSeoScore ?? ""}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        minSeoScore: e.target.value
                          ? parseInt(e.target.value)
                          : null,
                      }))
                    }
                  />
                </div>

                {/* Min GEO Score */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t("articles.filters.minGeoScore")}
                  </label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    placeholder={t("articles.filters.any")}
                    value={filters.minGeoScore ?? ""}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        minGeoScore: e.target.value
                          ? parseInt(e.target.value)
                          : null,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setFilters({
                      search: "",
                      status: null,
                      siteId: null,
                      dateRange: "all",
                      minSeoScore: null,
                      minGeoScore: null,
                    })
                  }
                >
                  {t("app.clearFilters")}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {selectedArticles.size > 0 && (
        <div className="flex items-center justify-between p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={selectedArticles.size === filteredArticles.length}
              onChange={selectAllArticles}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm font-medium">
              {t("articles.articlesSelected", { count: selectedArticles.size })}
            </span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="me-2 h-4 w-4" />
              {t("app.export")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkDelete}
              disabled={bulkDeleteMutation.isPending}
              className="text-destructive hover:text-destructive"
            >
              {bulkDeleteMutation.isPending ? (
                <Loader2 className="me-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="me-2 h-4 w-4" />
              )}
              {t("app.delete")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedArticles(new Set())}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {isLoading && <p className="text-muted-foreground">{t("app.loading")}</p>}

      {error && (
        <p className="text-destructive">
          {t("errors.errorLoadingArticles")} {(error as Error).message}
        </p>
      )}

      {filteredArticles && filteredArticles.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Sparkles className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">
              {t("articles.noArticlesFound")}
            </p>
            <p className="text-muted-foreground text-center mt-1 max-w-md">
              {filters.search ||
              filters.status ||
              filters.siteId ||
              filters.dateRange !== "all"
                ? t("articles.adjustFilters")
                : t("articles.letAIGenerate")}
            </p>
            <Button onClick={() => setViewMode("generate")} className="mt-4">
              <Sparkles className="me-2 h-4 w-4" />
              {t("articles.generateWithAI")}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Results count */}
      {filteredArticles.length > 0 && (
        <div className="text-sm text-muted-foreground">
          {t("articles.showingArticles", {
            shown: filteredArticles.length,
            total: data?.total || 0,
          })}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredArticles?.map((article) => {
          const status = getArticleStatus(article);
          const config = statusConfig[status];
          const StatusIcon = config.icon;
          const isGenerating = isArticleGenerating(status);
          const isSelected = selectedArticles.has(article.id);

          const handleCardClick = () => {
            setActiveArticleId(article.id);
            setActiveTopic(article.title);
            if (isGenerating) {
              setViewMode("progress");
            } else {
              setViewMode("view");
            }
          };

          return (
            <Card
              key={article.id}
              className={cn(
                "cursor-pointer hover:shadow-md transition-shadow relative",
                isGenerating && "border-blue-500/50",
                isSelected && "ring-2 ring-primary",
              )}
            >
              {/* Selection Checkbox */}
              <div
                className="absolute top-3 left-3 z-10"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleArticleSelection(article.id)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
              </div>

              <div onClick={handleCardClick}>
                <CardHeader className="pb-3 pl-10">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 pr-2">
                      <CardTitle className="line-clamp-2 text-lg">
                        {article.title}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {new Date(article.created_at).toLocaleDateString()}
                      </CardDescription>
                    </div>
                    <div className={`p-1.5 rounded-full ${config.color}`}>
                      <StatusIcon
                        className={`h-3 w-3 text-white ${
                          isGenerating ? "animate-spin" : ""
                        }`}
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                    {stripHtmlTags(article.content).substring(0, 200)}...
                  </p>

                  {/* Article metrics */}
                  {(article.word_count ||
                    article.seo_score ||
                    article.geo_score) && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mb-4">
                      {article.word_count > 0 && (
                        <span>
                          {article.word_count} {t("articles.words")}
                        </span>
                      )}
                      {article.seo_score !== null && (
                        <span>SEO: {article.seo_score}/100</span>
                      )}
                      {article.geo_score !== null && (
                        <span>GEO: {article.geo_score}/100</span>
                      )}
                      {article.human_score !== null && (
                        <span>Human: {article.human_score}%</span>
                      )}
                      {article.generation_cost > 0 && (
                        <span>${article.generation_cost.toFixed(2)}</span>
                      )}
                      {article.generation_time != null && (
                        <span>
                          {article.generation_time >= 60
                            ? `${Math.floor(article.generation_time / 60)}:${String(Math.floor(article.generation_time % 60)).padStart(2, "0")}`
                            : `${Math.floor(article.generation_time)}s`}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-1 mb-4">
                    {article.tags
                      .filter((tag) => !Object.keys(statusConfig).includes(tag))
                      .slice(0, 5)
                      .map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 text-xs bg-secondary rounded-md"
                        >
                          {tag}
                        </span>
                      ))}
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <StatusIcon
                        className={`h-3 w-3 ${isGenerating ? "animate-spin" : ""}`}
                      />
                      {t(config.labelKey)}
                    </span>
                    <div
                      className="flex gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {isGenerating && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setActiveArticleId(article.id);
                            setActiveTopic(article.title);
                            setViewMode("progress");
                          }}
                        >
                          <Eye className="me-1 h-3 w-3" />
                          {t("articles.progress")}
                        </Button>
                      )}
                      {status === "ready" && (
                        <Button variant="outline" size="sm">
                          <Send className="me-1 h-3 w-3" />
                          {t("articles.publish")}
                        </Button>
                      )}
                      {(status === "draft" || status === "failed") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (confirm(t("articles.confirmDelete"))) {
                              deleteArticle.mutate(article.id);
                            }
                          }}
                        >
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </div>
            </Card>
          );
        })}
      </div>

      {data && data.pages > 1 && (
        <div className="flex justify-center gap-2">
          <p className="text-sm text-muted-foreground">
            {t("articles.pagination.page", {
              current: data.page,
              total: data.pages,
              count: data.total,
            })}
          </p>
        </div>
      )}
    </div>
  );
}
